import { useEffect, useState } from "react";
import { Phone } from "@phosphor-icons/react";
import { Sheet, SheetContent, SheetTitle } from "../ui/sheet";
import { useScriptPanel } from "../../context/ScriptPanelContext";
import { SCRIPT_GUIDANCE, FUNDING_SCRIPT_SECTIONS, TRUST_SCRIPT_SECTIONS } from "./scriptContent";
import { api } from "../../lib/api";

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return isMobile;
}

export function ScriptPanel() {
  const panelCtx = useScriptPanel();
  const [tab, setTab] = useState("funding_call");
  const isMobile = useIsMobile();

  useEffect(() => {
    if (panelCtx?.open) setTab(panelCtx.defaultTab);
  }, [panelCtx?.open, panelCtx?.defaultTab]);

  if (!panelCtx) return null;
  const { open, setOpen, activeLead } = panelCtx;
  const sections = tab === "trust_call" ? TRUST_SCRIPT_SECTIONS : FUNDING_SCRIPT_SECTIONS;

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        side={isMobile ? "bottom" : "right"}
        data-testid="script-panel-drawer"
        className={`bg-igc-surface border-igc-gold/30 text-igc-cream p-0 flex flex-col gap-0 ${isMobile ? "h-[85vh] rounded-t-2xl w-full" : "w-[440px] sm:max-w-[440px] h-full"}`}
      >
        <SheetTitle className="sr-only">Call Scripts</SheetTitle>

        <div className="px-6 py-4 pr-14 border-b border-igc-gold/20 bg-igc-surface/80 flex items-center justify-between flex-shrink-0" data-testid="script-panel-lead-header">
          <div className="min-w-0" title="The name and phone number of the lead you're currently viewing scripts for.">
            <p className="text-igc-cream font-serif text-lg truncate" data-testid="script-panel-lead-name">{activeLead?.name || "Call Scripts"}</p>
            <p className="text-igc-goldText text-sm" data-testid="script-panel-lead-phone">{activeLead?.phone || "No lead selected — general reference"}</p>
          </div>
          {activeLead?.phone && (
            <a
              href={`tel:${activeLead.phone}`}
              data-testid="script-panel-call-button"
              title="Dials this lead's phone number directly from the script panel, so you can read the script while you're on the call."
              onClick={() => { if (activeLead?.id) api.post(`/rep/leads/${activeLead.id}/reveal-phone`, { portal_section: "script_panel" }).catch(() => {}); }}
              className="flex items-center gap-1.5 bg-igc-gold hover:bg-igc-goldHover text-black text-xs px-3 py-2 rounded-full flex-shrink-0 ml-3"
            >
              <Phone size={14} weight="fill" /> Call
            </a>
          )}
        </div>

        <div className="flex border-b border-igc-gold/20 px-6 flex-shrink-0" data-testid="script-panel-tabs">
          <button
            data-testid="script-tab-funding-call"
            onClick={() => setTab("funding_call")}
            title="Shows the word-for-word talking points to use on a Business Funding call."
            className={`py-3 px-4 text-sm font-medium tracking-wide transition-colors ${tab === "funding_call" ? "text-igc-goldText border-b-2 border-igc-gold" : "text-igc-muted hover:text-igc-cream"}`}
          >
            Funding Call
          </button>
          <button
            data-testid="script-tab-trust-call"
            onClick={() => setTab("trust_call")}
            title="Shows the word-for-word talking points to use on a Trust & Asset Protection call."
            className={`py-3 px-4 text-sm font-medium tracking-wide transition-colors ${tab === "trust_call" ? "text-igc-goldText border-b-2 border-igc-gold" : "text-igc-muted hover:text-igc-cream"}`}
          >
            Trust Call
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5" data-testid="script-panel-content">
          <div className="border-l-4 border-igc-gold bg-igc-gold/10 p-4 rounded-r text-igc-cream font-medium text-sm leading-relaxed mb-5 shadow-inner" data-testid="script-panel-guidance-box" title="General coaching tips on how to use these scripts naturally, so you don't sound like you're reading off a page.">
            {SCRIPT_GUIDANCE}
          </div>
          <div data-testid={`script-panel-script-${tab}`}>
            {sections.map((section, i) => (
              <div key={i} className="mb-5">
                <p className="text-igc-goldText font-serif font-semibold text-base uppercase tracking-wide border-b border-igc-gold/20 pb-1 mb-2">{section.heading}</p>
                {section.body.split("\n\n").map((para, j) => (
                  <p key={j} className="text-lg leading-relaxed text-igc-cream mb-2 whitespace-pre-line">{para}</p>
                ))}
              </div>
            ))}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
